/**
 * Shared timeline utility functions for impact report detection and parsing.
 *
 * Used by IncidentTimeline (detail view) and ExpandedContent (list view)
 * to identify and parse impact report messages in the timeline.
 */

/**
 * Checks whether a message string is an impact report by looking for
 * the Brand: / Application: / Status: pattern.
 *
 * @param {string} message - The note message text
 * @returns {boolean} True when the message matches the impact report pattern
 */
export function isImpactReport(message) {
  if (!message || typeof message !== 'string') return false;
  return (
    message.includes('Brand:') &&
    message.includes('Application:') &&
    message.includes('Status:')
  );
}

/**
 * Resolves the effective display type for a timeline event.
 * Notes whose message matches the impact report pattern are promoted
 * to 'impact-report'; all other events keep their original eventType.
 *
 * @param {Object} event - Timeline event with eventType and data.message
 * @returns {string} 'impact-report' or the original eventType
 */
export function resolveEventType(event) {
  if (event.eventType === 'note' && isImpactReport(event.data?.message)) {
    return 'impact-report';
  }
  return event.eventType;
}

/**
 * Parses an impact report message into structured fields.
 *
 * Extracts Brand, Application, and Status from multi-line format.
 * Handles different line break types: \n, \r\n, \r, or end of string.
 *
 * @param {string} message - The note message text
 * @returns {{ isImpactReport: boolean, brand?: string, application?: string, status?: string, message?: string }}
 */
export function parseImpactReport(message) {
  if (!message || typeof message !== 'string' || !message.includes('Brand:') || !message.includes('Application:') || !message.includes('Status:')) {
    return { isImpactReport: false, message: message ?? '' };
  }

  const brandMatch = message.match(/Brand:\s*(.+?)(?:\r?\n|\r|$)/);
  const appMatch = message.match(/Application:\s*(.+?)(?:\r?\n|\r|$)/);
  const statusMatch = message.match(/Status:\s*(.+?)(?:\r?\n|\r|$)/);

  if (brandMatch && appMatch && statusMatch) {
    return {
      isImpactReport: true,
      brand: brandMatch[1].trim(),
      application: appMatch[1].trim(),
      status: statusMatch[1].trim()
    };
  }

  return { isImpactReport: false, message: message ?? '' };
}

/**
 * Returns the CSS class name KEY for a timeline dot based on event type.
 * The returned string is intended to be used as a lookup key against a
 * CSS modules styles object, e.g. styles[dotClassKey(type)].
 *
 * @param {string} type - The resolved event type
 * @returns {string} CSS class name key
 */
export function dotClassKey(type) {
  switch (type) {
    case 'impact-report': return 'timelineDotImpact';
    case 'communication': return 'timelineDotCommunication';
    case 'workflow':      return 'timelineDotWorkflow';
    default:              return 'timelineDotNote';
  }
}

/**
 * Returns the CSS class name KEY for a timeline entry based on event type.
 * Used to annotate the entry container so tests and future styling hooks
 * can differentiate entries by type. Returns null for plain notes /
 * unknown types — callers must handle null in className construction.
 *
 * @param {string} type - The resolved event type
 * @returns {string|null} CSS class name key, or null for default
 */
export function entryLineClassKey(type) {
  switch (type) {
    case 'impact-report':  return 'timelineEntryImpact';
    case 'communication':  return 'timelineEntryCommunication';
    case 'workflow':       return 'timelineEntryWorkflow';
    default:               return null;
  }
}

